import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useTokenStorage } from 'src/composables/useTokenStorage';
import { api } from 'boot/axios';

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'alumno';
  isActive: boolean;
  avatarUrl: string | null;
  displayName: string | null;
  bio: string | null;
  phone: string | null;
  permissions: string[];
}

interface LoginResponse {
  token: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: 'admin' | 'alumno';
  };
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  dni?: string;
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const token = ref<string | null>(null);
  const user = ref<AuthUser | null>(null);

  // Token storage composable
  const { setToken: persistToken, removeToken } = useTokenStorage();

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value);
  const userRole = computed(() => user.value?.role ?? null);

  // Actions
  async function setToken(newToken: string) {
    token.value = newToken;
    await persistToken(newToken);
  }

  function setUser(newUser: AuthUser) {
    user.value = newUser;
  }

  async function clearAuth() {
    token.value = null;
    user.value = null;
    await removeToken();
  }

  function setAuth(newToken: string, newUser: AuthUser) {
    token.value = newToken;
    user.value = newUser;
  }

  async function login(email: string, password: string): Promise<void> {
    const response = await api.post<LoginResponse>('/auth/login', {
      email,
      password,
    });
    const { token: newToken } = response.data;
    await setToken(newToken);
    await fetchMe();
  }

  async function register(data: RegisterData): Promise<void> {
    const response = await api.post<LoginResponse>('/auth/register', data);
    const { token: newToken } = response.data;
    await setToken(newToken);
    await fetchMe();
  }

  async function fetchMe(): Promise<void> {
    try {
      const response = await api.get<AuthUser>('/auth/me');
      user.value = response.data;
    } catch {
      await clearAuth();
    }
  }

  async function updateProfile(data: {
    firstName?: string;
    lastName?: string;
    displayName?: string;
    phone?: string;
    bio?: string;
  }): Promise<void> {
    const response = await api.put<AuthUser>('/auth/me', data);
    user.value = response.data;
  }

  async function logout(): Promise<void> {
    await clearAuth();
  }

  function canAccess(resource: string): boolean {
    return user.value?.permissions.includes(resource) ?? false;
  }

  return {
    // State
    token,
    user,
    // Getters
    isAuthenticated,
    userRole,
    // Actions
    setToken,
    setUser,
    clearAuth,
    setAuth,
    login,
    register,
    fetchMe,
    updateProfile,
    logout,
    canAccess,
  };
});
